<template>
    <bottom-sheet v-model="shows" title="매칭 승인여부 확인" fullscreen persistent width="1080" maxWidth="95%" needsLogin>
        <template #activator="{ attrs, on }">
            <slot name="activator" v-bind="{ attrs, on, loading }" />
        </template>
        <v-layout column justify-center class="bottom-sheet__contents h-100">
            <template v-if="loading">
                <v-overlay absolute color="white">
                    <v-progress-circular indeterminate size="100" width="5" color="primary" />
                </v-overlay>
            </template>
            <template v-else>
                <div v-if="step == STEPS.RESULTS.value" class="d-flex flex-column">
                    <v-sheet class="d-flex flex-column align-center">
                        <i class="icon icon-check mb-20 mb-lg-40"></i>
                        <v-sheet class="text-center mb-20 mb-lg-40">
                            <h3 class="tit mb-8 mb-lg-16">
                                <template v-if="form.isGranted">
                                    <span> 매칭 성사 완료 </span>
                                </template>
                                <template v-else>
                                    <span> 매칭 거부 완료 </span>
                                </template>
                            </h3>
                            <p class="page-text--lg grey-6--text">
                                <template v-if="form.isGranted">
                                    매칭이 성사되었습니다. <br />
                                    면접기간에 면접진행 해주세요.
                                </template>
                                <template v-else>
                                    <span> 매칭이 거부되었습니다. </span>
                                </template>
                            </p>
                        </v-sheet>
                        <div class="v-btn-group mt-0">
                            <v-btn color="primary" class="min-w-140px min-w-lg-160px" @click="exit"><span>확인</span></v-btn>
                        </div>
                    </v-sheet>
                </div>
                <div v-else class="d-flex flex-column fill-height justify-center">
                    <div class="text-center mb-20 mb-lg-40">
                        <h3 class="tit mb-8 mb-lg-16">매칭 승인여부 확인</h3>
                        <p class="page-text--lg grey-6--text">
                            <span class="font-weight-bold">
                                {{ value.employer.info.name }}
                            </span>
                            기업의 면접 요청 승인여부를 선택해주세요.
                        </p>
                    </div>
                    <common-btns v-model="form" @input="emit" class="mb-20 mb-lg-40" style="flex: 0 1 auto" />
                    <v-sheet class="scroll-primary pr-10" style="flex: 0 1 auto; overflow-y: auto; overflow-x: hidden">
                        <v-stepper :value="`${form.isGranted}`">
                            <v-stepper-items>
                                <v-stepper-content :step="'true'" class="pa-0">
                                    <expo-matching-table
                                        v-model="form"
                                        v-bind="{
                                            program,
                                            employer: value.employer,
                                            matchings,
                                        }"
                                        class="mb-20 mb-lg-40"
                                        @input="emit"
                                    />
                                    <common-loca
                                        v-bind="{
                                            value,
                                        }"
                                        class="mb-20 mb-lg-40"
                                    />
                                    <employer-info />
                                </v-stepper-content>
                                <v-stepper-content :step="'false'" class="pa-0">
                                    <common-rmsg v-model="form" @input="emit" />
                                </v-stepper-content>
                            </v-stepper-items>
                        </v-stepper>
                    </v-sheet>
                    <div class="v-btn-group" style="flex: 0 1 auto">
                        <v-btn outlined color="grey-d" class="min-w-140px min-w-lg-160px" @click="exit">
                            <span class="grey-6--text"> 취소하기 </span>
                        </v-btn>
                        <v-btn color="primary" class="min-w-140px min-w-lg-160px" @click="save">
                            <span> 응답하기 </span>
                        </v-btn>
                    </div>
                </div>
            </template>
        </v-layout>
    </bottom-sheet>
</template>

<script>
import api from "@/api";
import { initExpoMatching, initExpoProgram } from "@/assets/variables/inits";

import BottomSheet from "@/components/dumb/bottom-sheet.vue";
import ExpoMatchingTable from "@/components/client/expo/matching/expo-matching-table.vue";
import EmployerInfo from "./employer/employer-info.vue";
import CommonBtns from "./common/common-btns.vue";
import CommonLoca from "./common/common-loca.vue";
import CommonRmsg from "./common/common-rmsg.vue";

const STEPS = {
    RESPOND: { value: "RESPOND", text: "승인" },
    RESULTS: { value: "RESULTS", text: "결과" },
};

const steps = Object.values(STEPS).map((item, index) => ({ ...item, step: index + 1 }));

export default {
    components: {
        BottomSheet,
        ExpoMatchingTable,
        EmployerInfo,
        CommonBtns,
        CommonLoca,
        CommonRmsg,
    },
    props: {
        value: { type: Object, default: initExpoMatching },
        program: { type: Object, default: initExpoProgram },

        isGranted: { type: Boolean, default: undefined },
    },
    data: () => ({
        form: initExpoMatching(),
        matchings: [],

        shows: false,
        loading: false,
        updated: false,

        step: steps[0].value,
        steps,
        STEPS,
    }),
    computed: {
        _program() {
            return this.program._id;
        },
        _employee() {
            return this.value?.employee?._id;
        },
        _employer() {
            return this.value?.employer?._id;
        },
    },
    methods: {
        async init() {
            this.updated = false;

            if (this.loading) return;
            else this.loading = true;

            this.step = steps[0].value;

            try {
                let { matching } = await api.v1.person.expo.matchings.get({
                    _id: this.value._id,
                });
                if (this.isGranted !== undefined) {
                    matching.isGranted = this.isGranted;
                }
                this.form = initExpoMatching(matching);

                const { _program, _employee, _employer } = this;
                const isGranted = JSON.stringify({ $in: [null, true] });

                let { matchings: matchings_employee } = await api.v1.expo.programs.matchings.gets({
                    _program,
                    params: {
                        _employee,
                        isGranted,
                    },
                });

                let { matchings: matchings_employer } = await api.v1.expo.programs.matchings.gets({
                    _program,
                    params: {
                        _employer,
                        isGranted,
                    },
                });

                this.matchings = [...matchings_employer, ...matchings_employee];
            } finally {
                this.loading = false;
            }
        },
        async exit() {
            this.shows = false;
        },
        async next() {
            if (!this.validate()) return;

            let index = this.steps.findIndex(({ value }) => value == this.step);
            this.step = this.steps[index + 1]?.value;
        },
        async prev() {
            let index = this.steps.findIndex(({ value }) => value == this.step);
            this.step = this.steps[index - 1]?.value;
        },
        async save() {
            if (!this.validate()) return;

            if (this.loading) return;
            else this.loading = true;
            try {
                let { ...form } = this.form;

                if (form.isGranted) {
                    form.grantedAt = Date.now();

                    delete form.refuseMsg;
                    delete form.refusedAt;
                } else {
                    form.refusedAt = Date.now();
                    delete form.grantedAt;
                }

                let { matching } = await api.v1.person.expo.matchings.put(form);
                this.form = initExpoMatching(matching);
                this.updated = true;
                this.next();
            } finally {
                if (this.loading) this.loading = false;
            }
        },

        emit() {
            this.form = initExpoMatching(this.form);
        },

        validate() {
            try {
                switch (this.step) {
                    case STEPS.RESPOND.value: {
                        if (this.form.isGranted) {
                            if (!this.form.interviewAt) {
                                throw new Error("면접 시간을 선택해주세요");
                            }
                        }
                        //
                        else {
                            if (!this.form.refuseMsg) {
                                throw new Error("거부 사유를 작성해주세요");
                            }
                        }
                        break;
                    }
                }

                return true;
            } catch (error) {
                alert(error.message);
                return false;
            }
        },
    },
    watch: {
        _employee() {
            this.init();
        },
        _employer() {
            this.init();
        },
        _program() {
            this.init();
        },
        _user() {
            this.init();
        },
        shows(shows) {
            if (shows) this.$nextTick(this.init);
            else if (this.updated) {
                this.$emit("input", this.form);
            }
        },
    },
    mounted() {
        this.init();
    },
};
</script>

<style lang="scss" scoped>
::v-deep .v-stepper__wrapper {
    display: flex;
    flex-direction: column;
}
</style>
